import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../api.js";

interface DuplicateSide {
  id: string;
  name: string;
  club: string | null;
  country: string | null;
  tapologyUrl: string | null;
  fights: { event: { name: string; date: string }; opponent: { name: string } }[];
}

interface DuplicatePair {
  a: DuplicateSide;
  b: DuplicateSide;
}

interface FighterRow {
  id: string;
  name: string;
  club: string | null;
  country: string | null;
  heightCm: number | null;
  reachCm: number | null;
  tapologyUrl: string | null;
  fightCount: number;
}

const norm = (s: string) =>
  s.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();

export function AdminFightersPage() {
  const [fighters, setFighters] = useState<FighterRow[] | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicatePair[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const [f, d] = await Promise.all([
      api.get<FighterRow[]>("/api/fighters"),
      api.get<DuplicatePair[]>("/api/fighters/duplicates"),
    ]);
    setFighters(f);
    setDuplicates(d);
  }
  useEffect(() => {
    load();
  }, []);

  async function merge(keep: DuplicateSide, remove: DuplicateSide) {
    const ok = confirm(
      `Læg "${remove.name}" sammen med "${keep.name}"?\n\n` +
        `${remove.fights.length} ${remove.fights.length === 1 ? "kamp flyttes" : "kampe flyttes"} til "${keep.name}". ` +
        `Navn og udfyldte felter fra "${keep.name}" beholdes; tomme felter udfyldes fra "${remove.name}". ` +
        `Profilen "${remove.name}" slettes.\n\nStats og resultater bevares. Kan ikke fortrydes.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/api/fighters/${keep.id}/merge`, { fromId: remove.id });
      await load();
    } catch (err) {
      alert(errorMessage(err, "Profilerne kunne ikke lægges sammen"));
    } finally {
      setBusy(false);
    }
  }

  const shown = (fighters ?? []).filter((f) =>
    norm(`${f.name} ${f.club ?? ""} ${f.country ?? ""}`).includes(norm(q.trim())),
  );

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Fightere</h1>
        <Link to="/admin" className="text-sm text-neutral-400 hover:text-neutral-200">
          ← Events
        </Link>
      </header>

      <p className="text-sm text-neutral-400">
        Én profil pr. person på tværs af events. Navn, klub, land, højde og
        rækkevidde følger fighteren; record, indvejning og hjørne hører til den
        enkelte kamp.
      </p>

      {duplicates.length > 0 && (
        <section className="bg-amber-950/30 border border-amber-800 rounded-xl p-4 space-y-3">
          <div>
            <h2 className="font-semibold text-amber-300">
              {duplicates.length} mulige dubletter
            </h2>
            <p className="text-xs text-neutral-400">
              Profiler med ens navne eller kun efternavn. Tjek kampene og vælg
              hvilken profil der skal beholdes — eller lad parret være, hvis det
              er to forskellige personer.
            </p>
          </div>
          {duplicates.map(({ a, b }) => (
            <div key={`${a.id}-${b.id}`} className="grid grid-cols-2 gap-3">
              {(
                [
                  [a, b],
                  [b, a],
                ] as const
              ).map(([keep, remove]) => (
                <div
                  key={keep.id}
                  className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 flex flex-col gap-2"
                >
                  <div>
                    <Link to={`/admin/fighters/${keep.id}`} className="font-medium hover:underline">
                      {keep.name}
                    </Link>
                    <div className="text-xs text-neutral-400">
                      {[keep.club, keep.country].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {keep.fights.map((f, i) => (
                      <div key={i} className="text-xs text-neutral-500">
                        {f.event.name.replace(/^MMA Galla /, "")} mod {f.opponent.name}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => merge(keep, remove)}
                    disabled={busy}
                    className="mt-auto text-xs px-3 py-1.5 bg-neutral-700 hover:bg-neutral-600 disabled:opacity-40 rounded"
                  >
                    Behold "{keep.name}"
                  </button>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      <input
        placeholder="Søg navn, klub eller land"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full bg-neutral-800 rounded px-3 py-2"
      />

      {!fighters && <div className="text-neutral-400 text-sm">Indlæser…</div>}
      <div className="bg-neutral-900 rounded-xl border border-neutral-800 divide-y divide-neutral-800">
        {shown.map((f) => (
          <Link
            key={f.id}
            to={`/admin/fighters/${f.id}`}
            className="flex items-center gap-3 px-4 py-2 hover:bg-neutral-800"
          >
            <span className="flex-1 font-medium">{f.name}</span>
            <span className="text-xs text-neutral-400">
              {[f.club, f.country].filter(Boolean).join(" · ") || "—"}
            </span>
            <span className="text-xs text-neutral-500 w-16 text-right">
              {f.fightCount} {f.fightCount === 1 ? "kamp" : "kampe"}
            </span>
          </Link>
        ))}
        {fighters && shown.length === 0 && (
          <div className="px-4 py-3 text-sm text-neutral-500">Ingen fightere fundet.</div>
        )}
      </div>
    </div>
  );
}
